import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {Router} from 'react-router-dom';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';

import {nutApolloClient} from '../../apollo-client';
import {appHistory} from '../../history';
import {store} from '../../store';

import {CallTranscribeButton} from '../click-to-call/call-transcribe-button';

import {ActivityModalSummarizeButton} from './activity-modal-summarize-button';

/**
 * This component listens for when the activity modal loads, and then renders
 * the summarize button in the activity modal if it is a zoom meeting with a
 * transcript, and/or the transcribe button if it is a phone call with a
 * recording.
 */
export function ActivityModalLegacyEventListeners() {
    const [activityId, setActivityId] = React.useState<string | null | undefined>(null);
    const [hasRef, setHasRef] = React.useState<boolean>(false);
    const [hasTranscribeRef, setHasTranscribeRef] = React.useState<boolean>(false);
    const [hasTranscription, setHasTranscription] = React.useState<boolean>(false);

    // hasMounted exists to force a re-run of the useEffect that renders the
    // summarize button. This is necessary because none of the other
    // dependencies in that useEffect change when the modal is closed and
    // re-opened, but we need to force a re-mount to ensure that the ref does
    // not become stale in that scenario.
    const [hasMounted, setHasMounted] = React.useState<boolean>(false);
    const summarizeMountRef = React.useRef();
    const transcribeMountRef = React.useRef();

    const handleModalWasOpened = (
        e: any,
        data: {
            activityId: string;
        }
    ) => {
        setActivityId(data.activityId);
        setHasMounted(false);
    };

    React.useEffect(() => {
        $(window).on('activityModalDidLoad', handleModalWasOpened);

        return () => {
            $(window).off('activityModalDidLoad', handleModalWasOpened);
        };
    }, []);

    React.useEffect(() => {
        if (!hasMounted) {
            const summarizeMount = document.querySelector('#summarize-button-mount');
            const transcribeMount = document.querySelector('#call-transcribe-button-mount');
            if (summarizeMount) {
                // @ts-expect-error
                summarizeMountRef.current = summarizeMount;
                setHasRef(true);
            }
            if (transcribeMount) {
                // @ts-expect-error
                transcribeMountRef.current = transcribeMount;
                setHasTranscribeRef(true);
            }
            setHasMounted(true);
        }
    }, [activityId, hasTranscription, hasMounted]);

    React.useEffect(() => {
        if (
            NutClientConfig.enableAi &&
            activityId &&
            hasRef &&
            summarizeMountRef &&
            summarizeMountRef.current &&
            hasMounted
        ) {
            ReactDOM.render(
                <Provider store={store}>
                    <ApolloProvider client={nutApolloClient}>
                        <ReactHooksApolloProvider client={nutApolloClient}>
                            <Router history={appHistory}>
                                <ActivityModalSummarizeButton
                                    activityId={`${activityId}-activities`}
                                />
                            </Router>
                        </ReactHooksApolloProvider>
                    </ApolloProvider>
                </Provider>,
                summarizeMountRef.current
            );
        }
    }, [activityId, hasRef, summarizeMountRef, hasTranscription, hasMounted]);

    React.useEffect(() => {
        if (
            NutClientConfig.enableAi &&
            activityId &&
            hasTranscribeRef &&
            transcribeMountRef &&
            transcribeMountRef.current &&
            hasMounted
        ) {
            ReactDOM.render(
                <Provider store={store}>
                    <ApolloProvider client={nutApolloClient}>
                        <ReactHooksApolloProvider client={nutApolloClient}>
                            <Router history={appHistory}>
                                <CallTranscribeButton
                                    activityId={`${activityId}-activities`}
                                    setHasTranscription={setHasTranscription}
                                />
                            </Router>
                        </ReactHooksApolloProvider>
                    </ApolloProvider>
                </Provider>,
                transcribeMountRef.current
            );
        }
    }, [hasTranscribeRef, transcribeMountRef, hasMounted, activityId]);

    return <></>;
}
